import { defaultAddStatus, defaultAddOwnership } from "../utils/settings.js";
import { getDB } from "./schema.js";
import { todayDate, nowIso } from "../utils/dates.js";
import { queueCloudWrite } from "./sync.js";
import { logItemChange } from "./history.js";
import { TIMES_FINISHED, AWARDS } from "../components/status-badge.js";
import { catalogView, applyCatalogFields } from "../utils/overrides.js";

const STORE = "games";

function uuid() {
	if (globalThis.crypto?.randomUUID) return crypto.randomUUID();
	return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
		const r = (Math.random() * 16) | 0;
		const v = c === "x" ? r : (r & 0x3) | 0x8;
		return v.toString(16);
	});
}

export async function listGames() {
	const db = await getDB();
	return db.getAll(STORE);
}

export async function getGameById(id) {
	const db = await getDB();
	return db.get(STORE, id);
}

export async function getGameByIgdbId(igdbId) {
	const db = await getDB();
	return db.getFromIndex(STORE, "igdb_id", igdbId);
}

export async function addGame({
	igdb_id,
	title,
	release_year = null,
	release_date = null,
	release_precision = null,
	cover_url = null,
	platforms = [],
	genres = [],
	summary = null,
	franchise = null,
	collection = null,
	media_type = "videogame",
	engagement_status = defaultAddStatus(),
	ownership_status = defaultAddOwnership(),
	formats = [],
}) {
	const existing = await getGameByIgdbId(igdb_id);
	if (existing) return existing;

	const now = nowIso();
	const today = todayDate();
	const record = {
		id: uuid(),
		igdb_id,
		title,
		release_year,
		release_date,
		release_precision,
		cover_url,
		platforms,
		owned_on: [],
		genres,
		summary,
		franchise,
		collection,
		media_type,
		engagement_status,
		times_finished: null,
		// Canonical column the app no longer uses (times_finished replaced it);
		// kept at 0 so the synced cloud row stays valid.
		playthrough_count: 0,
		date_first_completed: engagement_status === "done" ? today : null,
		last_engaged_date:
			engagement_status === "playing" ||
			engagement_status === "replaying" ||
			engagement_status === "done"
				? today
				: null,
		rating: null,
		ownership_status,
		formats,
		note: null,
		// Specific editions the user owns (Standard / Deluxe / GOTY …). Platforms
		// (owned_on) already track which platform you own it on; owned_editions is
		// the finer "which edition". Empty until picked on the detail screen.
		owned_editions: [],
		awards: [],
		// Forward-looking; no UI yet (friends are deferred).
		visibility: "friends",
		created_at: now,
		updated_at: now,
	};
	const db = await getDB();
	await db.add(STORE, record);
	await queueCloudWrite({ type: "create", mediaType: "videogame", record });
	await logItemChange("videogame", null, record);
	return record;
}

export async function updateGame(id, patch) {
	const db = await getDB();
	const current = await db.get(STORE, id);
	if (!current) throw new Error(`Game ${id} not found`);

	const next = { ...current, ...patch, updated_at: nowIso() };

	if (
		patch.engagement_status &&
		patch.engagement_status !== current.engagement_status
	) {
		const today = todayDate();
		if (
			patch.engagement_status === "playing" ||
			patch.engagement_status === "replaying"
		) {
			next.last_engaged_date = today;
		}
		if (patch.engagement_status === "done") {
			next.last_engaged_date = today;
			if (!current.date_first_completed) {
				next.date_first_completed = today;
			}
		}
	}

	await db.put(STORE, next);
	await queueCloudWrite({ type: "update", mediaType: "videogame", record: next });
	await logItemChange("videogame", current, next);
	return next;
}

export async function setOwnedPlatforms(id, list) {
	if (!Array.isArray(list)) throw new Error("owned_on must be an array");
	return updateGame(id, { owned_on: list });
}

export const NOTE_MAX_LENGTH = 280;

export async function setNote(id, note) {
	// Keep it a note, not a review: trim and cap. Empty string clears it.
	const trimmed = (note ?? "").trim().slice(0, NOTE_MAX_LENGTH);
	return updateGame(id, { note: trimmed === "" ? null : trimmed });
}

export async function setFormats(id, list) {
	if (!Array.isArray(list)) throw new Error("formats must be an array");
	return updateGame(id, { formats: list });
}

// Keep only the fields we rely on, so a stored owned-edition is a stable, small
// snapshot regardless of what the editions endpoint adds later.
function sanitizeGameEdition(e) {
	if (!e || typeof e !== "object" || e.edition_id == null) return null;
	return {
		edition_id: e.edition_id,
		label: e.label ?? null,
		release_year: Number.isInteger(e.release_year) ? e.release_year : null,
		cover_url: e.cover_url ?? null,
		platforms: Array.isArray(e.platforms) ? e.platforms.filter(Boolean) : [],
	};
}

export async function setGameOwnedEditions(id, list) {
	if (!Array.isArray(list)) throw new Error("owned_editions must be an array");
	const clean = list.map(sanitizeGameEdition).filter(Boolean);
	return updateGame(id, { owned_editions: clean });
}

export async function setRating(id, rating) {
	if (
		rating !== null &&
		(!Number.isInteger(rating) || rating < 1 || rating > 5)
	) {
		throw new Error("Rating must be 1-5 or null");
	}
	return updateGame(id, { rating });
}

export async function setTimesFinished(id, value) {
	if (value !== null && !TIMES_FINISHED.some((b) => b.value === value)) {
		throw new Error(`Invalid times_finished bucket: ${value}`);
	}
	return updateGame(id, { times_finished: value });
}

export async function setAwards(id, list) {
	if (!Array.isArray(list)) throw new Error("awards must be an array");
	const valid = new Set(AWARDS.map((a) => a.value));
	const clean = list.filter((v) => valid.has(v));
	return updateGame(id, { awards: clean });
}

export async function deleteGame(id) {
	const db = await getDB();
	// Read first so the cloud delete op can carry the external id (igdb_id).
	const current = await db.get(STORE, id);
	await db.delete(STORE, id);
	if (current) {
		await queueCloudWrite({ type: "delete", mediaType: "videogame", record: current });
		await logItemChange("videogame", current, null);
	}
}

export async function deleteGameByIgdbId(igdbId) {
	const existing = await getGameByIgdbId(igdbId);
	if (!existing) return false;
	await deleteGame(existing.id);
	return true;
}

// Pure: what changes when a fresh IGDB record arrives for a tracked game. Release
// dates are AUTHORITATIVE from the catalog — a postponed or newly announced date
// overwrites what we stored, and a date IGDB has since dropped clears to null
// ("TBD"). franchise/collection only fill in when missing (pre-v2 records never
// captured them). Returns null when nothing would change.
export function gameCatalogPatch(current, fresh) {
	if (!current || !fresh) return null;
	// Belt and braces: never apply another game's record.
	if (
		fresh.igdb_id != null &&
		current.igdb_id != null &&
		String(fresh.igdb_id) !== String(current.igdb_id)
	) {
		return null;
	}
	const patch = {};
	if ("release_date" in fresh) {
		const date = fresh.release_date ?? null;
		if (date !== (current.release_date ?? null)) patch.release_date = date;
	}
	if ("release_year" in fresh) {
		const year = fresh.release_year ?? null;
		if (year !== (current.release_year ?? null)) patch.release_year = year;
	}
	// Precision moves with the date ("2027" → "Q4 2027" → "21 Oct 2027").
	if ("release_precision" in fresh) {
		const p = fresh.release_precision ?? null;
		if (p !== (current.release_precision ?? null)) patch.release_precision = p;
	}
	if (current.franchise == null && fresh.franchise) patch.franchise = fresh.franchise;
	if (current.collection == null && fresh.collection)
		patch.collection = fresh.collection;
	return Object.keys(patch).length > 0 ? patch : null;
}

// Apply a fresh IGDB record to a tracked game (see gameCatalogPatch). Writes —
// and syncs — only when something actually changed, so a daily re-check of an
// unchanged game costs no cloud traffic. Resolves { record, changed }, or null if
// the game is gone.
export async function applyCatalogRefresh(id, fresh) {
	const db = await getDB();
	const current = await db.get(STORE, id);
	if (!current) return null;
	// Diff against what the catalog last said (not the user's edits), then write
	// through the override layer: an edited field keeps the user's value and only
	// its source_values entry follows IGDB (utils/overrides.js).
	const patch = gameCatalogPatch(catalogView(current), fresh);
	if (!patch) return { record: current, changed: false };
	const { next: applied, changed } = applyCatalogFields(current, patch);
	if (!changed) return { record: current, changed: false };
	const next = { ...applied, updated_at: nowIso() };
	await db.put(STORE, next);
	await queueCloudWrite({ type: "update", mediaType: "videogame", record: next });
	return { record: next, changed: true };
}
